import React, { useEffect, useState, useContext, useCallback, useDebugValue } from 'react';
import { Box, Button, Checkbox, Grid, List, ListItemButton, ListItemIcon, ListItemText, Paper, Switch } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { LanguageContext } from '../App';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getPropriedadesVinculadas, getUsuario, updateUsuarioPropriedades, updateUsuarioStatus } from '../apis/apiUsuario';
import TransferListCustom from '../components/TransferListCustom';
import { getClientes } from '../apis/apiCliente';
import { getAllPropriedades } from '../apis/apiPropriedades';
import ComboBox from '../components/ComboBoxtCustom';
import warningIcon from '../assets/imgs/warningIcon.svg';

function not(a: readonly Propriedade[], b: readonly Propriedade[]) {
  return a.filter((value) => !b.map(value => value.proCodigo).includes(value.proCodigo));
}

function useNamedState<T>(name: string, initial: T) {
  const [value, setValue] = useState(initial);
  useDebugValue(`${name}: ${JSON.stringify(value)}`);
  return [value, setValue] as const;
}

function UsuarioPropriedade() {
  const [loading, setLoading] = useState<boolean>(true)
  const [searchParams] = useSearchParams();
  const { translations, setLanguage } = useContext(LanguageContext);
  const [usuario, setUsuario] = useState<Usuario>();
  const [propriedades, setPropriedades] = useState<Propriedade[]>();
  const [propriedadesVinculadas, setPropriedadesVinculadas] = useState<UsuarioPropriedade[]>();
  const [propriedadesOrigem, setPropriedadesOrigem] = useState<UsuarioPropriedade[]>();
  const [cliCodigoVinculados, setCliCodigoVinculados] = useState<(string | number)[]>();
  const [usuMaster, setUsuMaster] = useState<boolean>(false);
  const [valueComboLeft, setValueComboLeft] = useState<any>(null)
  const [valueComboRight, setValueComboRight] = useState<any>(null)
  const [ clienteCombo, setClienteCombo ] = useState<Combo[]>([{
    label: "",
    id: "",
    key: ""
  }]);
  const [checkedLeft, setCheckedLeft] = useNamedState<readonly Propriedade[]>("checkedLeft",[]);
  const [checkedRight, setCheckedRight] = useNamedState<readonly Propriedade[]>("checkedRight",[]);
  const [left, setLeft] = useNamedState<readonly Propriedade[]>("left",[]);
  const [right, setRight] = useNamedState<readonly Propriedade[]>("right",[]);

  const handleSavePropriedades= useCallback(async () => {
    const token = searchParams.get('token')
    const usuCodigo = searchParams.get('usu_codigo') || "";
    const language = searchParams.get('language') || 'pt'

    const mappedData = propriedadesVinculadas!.filter(propriedade => propriedade.proOrigem === false).map(propriedade => ({
      proCodigo: propriedade.proCodigo,
      cliCodigo: propriedade.cliCodigo
    }))

    let result = await updateUsuarioPropriedades(mappedData, token, usuCodigo)

    if (result.status === 201) {
      window.location.href = `../listar/usuario?token=${token}&language=${language}`
    }
  }, [propriedadesVinculadas, searchParams])

  const handleGetUsuario = useCallback(async () => {
    const usuCodigo = searchParams.get('usu_codigo') || "";

    if (usuCodigo.length > 0) {

      const token = searchParams.get('token')

      const result = await getUsuario(token, usuCodigo);

      if (result.status === 200) {        
        setUsuario(result.data)
        setUsuMaster(result.data.usuMaster)
      }

    }
    
  }, [searchParams]);

  const handleGetClientes = useCallback(async () => {

    const token = searchParams.get('token')

    const result = await getClientes(token);

    if (result.status === 200) {
      const cliCombo = result.data.map((cliente: Cliente) => ({
        label: `${cliente.cliCodigo} - ${cliente.cliNome}`,
        id: cliente.cliCodigo,
        key: cliente.cliCodigo
      }))
  
      setClienteCombo(cliCombo)      
    }

    
  }, [searchParams]);

  const handleGetPropriedades = useCallback(async (propriedadesVinculadas: Propriedade[]) => {

    const token = searchParams.get('token')

    const result = await getAllPropriedades(token);


    if (propriedadesVinculadas) {
      setPropriedades(result)
      const proCodigosVinculados = propriedadesVinculadas?.map((propriedade: Propriedade) => propriedade.proCodigo)
      setLeft(result.filter((propriedade: Propriedade) => proCodigosVinculados.includes(propriedade.proCodigo)))
      setRight(result.filter((propriedade: Propriedade) => !proCodigosVinculados.includes(propriedade.proCodigo)))
    } 
    
  }, [searchParams, setLeft, setRight]);

  const handleGetPropriedadesUsuario = useCallback(async () => {
    const usuCodigo = searchParams.get('usu_codigo') || "";

    if (usuCodigo.length > 0) {

      const token = searchParams.get('token')

      const result = await getPropriedadesVinculadas(token, usuCodigo);

      if (result.status === 200) {        
        setPropriedadesVinculadas(result.data)
        setPropriedadesOrigem(result.data.filter((propriedade:UsuarioPropriedade) => propriedade.proOrigem === true))
        setCliCodigoVinculados(result.data.map(((propriedade:UsuarioPropriedade) => propriedade.cliCodigo)))
        await handleGetClientes();
        await handleGetPropriedades(result.data);
      }

    }
    
  }, [handleGetClientes, handleGetPropriedades, searchParams]);

  const handleApis = useCallback(async () => {
    setLoading(true);
    try {
      await handleGetUsuario();
      await handleGetPropriedadesUsuario();
    } catch (error) {
      console.error("Erro ao realizar as requisições:", error);
    } finally {
      setLoading(false);
    }
  }, [handleGetPropriedadesUsuario, handleGetUsuario])

  useEffect(() => {
    const languageParam = searchParams.get('language') || 'pt'
    setLanguage(languageParam)
    handleApis()
  }, [handleApis, searchParams, setLanguage]);

  const handleUpdateUsuarioStatus = async () => {
    setUsuMaster(!usuMaster)

    if (usuario!.usuCodigo) {

      const token = searchParams.get('token')

      const result = await updateUsuarioStatus({ usuMaster: !usuMaster, usuCodigo: usuario!.usuCodigo}, token);

      if (result.status === 200) {
        handleGetUsuario();
      }
    }
  }

  const handleToggleLeft = (value: Propriedade) => () => {
    const currentIndex = checkedLeft.indexOf(value);
    const newChecked = [...checkedLeft];
    if (!(propriedadesVinculadas!.some((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proOrigem === true && usuarioPropriedade.proCodigo === value.proCodigo))) {
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setCheckedLeft(newChecked);
    }
  };

    const handleToggleRight = (value: Propriedade) => () => {
    const currentIndex = checkedRight.indexOf(value);
    const newChecked = [...checkedRight];
    if (!(propriedadesVinculadas!.some((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proOrigem === true && usuarioPropriedade.proCodigo === value.proCodigo))) {
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setCheckedRight(newChecked);
    }
  };

  const handleAllRight = () => {
    handleLeftComboChange(null, null)
    const proCodigoOrigem = propriedadesVinculadas?.filter((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proOrigem === true).map(item => item.proCodigo)
    const proCodigosVinculados = propriedadesVinculadas?.map((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proCodigo)
    const newLeft = propriedades!.filter((propriedade: Propriedade) => proCodigosVinculados!.includes(propriedade.proCodigo))
    const propriedadesOrigem = newLeft.filter((propriedade:Propriedade) => proCodigoOrigem?.includes(propriedade.proCodigo));
    const propriedadesTransferidas = newLeft.filter((propriedade:Propriedade) => !proCodigoOrigem?.includes(propriedade.proCodigo));
    setRight(right.concat(propriedadesTransferidas));
    setLeft(propriedadesOrigem);
    setPropriedadesVinculadas(propriedadesVinculadas?.filter((usuarioPropriedade:UsuarioPropriedade) => propriedadesOrigem.map((propriedade:Propriedade) => propriedade.proCodigo).includes(usuarioPropriedade.proCodigo)))
    setCliCodigoVinculados(cliCodigoVinculados?.filter((cliCodigo: any) => propriedadesOrigem.map((propriedade:Propriedade) => propriedade.cliCodigo).includes(cliCodigo)))
  };

  const handleCheckedRight = () => {
    handleLeftComboChange(null, null)
    const proCodigosVinculados = propriedadesVinculadas?.map((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proCodigo)
    const newLeft = propriedades!.filter((propriedade: Propriedade) => proCodigosVinculados!.includes(propriedade.proCodigo))
    setRight(right.concat(checkedLeft));
    setLeft(not(newLeft, checkedLeft));
    setCheckedLeft([]);
    setPropriedadesVinculadas(propriedadesVinculadas?.filter((usuarioPropriedade:UsuarioPropriedade) => !checkedLeft.map((propriedade:Propriedade) => propriedade.proCodigo).includes(usuarioPropriedade.proCodigo)))
    setCliCodigoVinculados(cliCodigoVinculados?.filter((cliCodigo: any) => !checkedLeft.map((propriedade:Propriedade) => propriedade.cliCodigo).includes(cliCodigo)))
  };

  const handleCheckedLeft = () => {
    handleRightComboChange(null, null)
    const proCodigosVinculados = propriedadesVinculadas?.map((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proCodigo)
    const newRight = propriedades!.filter((propriedade: Propriedade) => !proCodigosVinculados!.includes(propriedade.proCodigo))
    setLeft(left.concat(checkedRight));
    setRight(not(newRight, checkedRight));
    setCheckedRight([]);
    setPropriedadesVinculadas((left.concat(checkedRight)).map((propriedade:Propriedade) => ({
      proCodigo: propriedade.proCodigo,
      cliCodigo: propriedade.cliCodigo,
      proOrigem: propriedadesOrigem!.some((usuarioPropriedade:UsuarioPropriedade) => usuarioPropriedade.proCodigo === propriedade.proCodigo),
    })))
    setCliCodigoVinculados((left.concat(checkedRight)).map((propriedade:Propriedade) => propriedade.cliCodigo))
  };

  const handleAllLeft = () => {
    handleRightComboChange(null, null)
    const proCodigosVinculados = propriedadesVinculadas?.map((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proCodigo)
    const newRight = propriedades!.filter((propriedade: Propriedade) => !proCodigosVinculados!.includes(propriedade.proCodigo))
    setLeft(left.concat(newRight));
    setRight([]);
    setPropriedadesVinculadas(((left.concat(newRight)).map((propriedade:Propriedade) => ({
      proCodigo: propriedade.proCodigo,
      cliCodigo: propriedade.cliCodigo,
      proOrigem: propriedadesOrigem!.some((usuarioPropriedade:UsuarioPropriedade) => usuarioPropriedade.proCodigo === propriedade.proCodigo),
    }))))
    setCliCodigoVinculados((left.concat(newRight)).map((propriedade:Propriedade) => propriedade.cliCodigo))
  };

  const handleLeftComboChange = (_event: any, newValue: any) => {
    const proCodigosVinculados = propriedadesVinculadas?.map((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proCodigo)
    if (newValue) {
      setLeft(propriedades!.filter((propriedade: Propriedade) => propriedade.cliCodigo === newValue.id && proCodigosVinculados!.includes(propriedade.proCodigo)))
    } else {
      setLeft(propriedades!.filter((propriedade: Propriedade) => proCodigosVinculados!.includes(propriedade.proCodigo)))
    }
    setValueComboLeft(newValue)
  }

  const handleRightComboChange = (_event: any, newValue: any) => {
    const proCodigosVinculados = propriedadesVinculadas?.map((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proCodigo)
    if (newValue) {
      setRight(propriedades!.filter((propriedade: Propriedade) => propriedade.cliCodigo === newValue.id && !proCodigosVinculados!.includes(propriedade.proCodigo)))
    } else {
      setRight(propriedades!.filter((propriedade: Propriedade) => !proCodigosVinculados!.includes(propriedade.proCodigo)))
      console.log("Entrou", right)
    }
    setValueComboRight(newValue)
  }

  const customList = (items: Propriedade[], handleToggle: any, checked: Propriedade[]) => (
    <Paper sx={{ width: "100%", height: 600, overflow: 'auto' }}>
      <List dense component="div" role="list">
        { items.length ?
          items.sort((propriedadeA: Propriedade, propriedadeB: Propriedade) => {
            const aChecked = checked.includes(propriedadeA);
            const bChecked = checked.includes(propriedadeB);
            if (aChecked === bChecked) return 0;
            return aChecked ? -1 : 1; 
          }).sort((propriedadeA: Propriedade, propriedadeB: Propriedade) => {
            const aOrigem = propriedadesOrigem!.some((propriedade:UsuarioPropriedade) => propriedade.proCodigo === propriedadeA.proCodigo);
            const bOrigem = propriedadesOrigem!.some((propriedade:UsuarioPropriedade) => propriedade.proCodigo === propriedadeB.proCodigo);
            if (aOrigem === bOrigem) return 0;
            return aOrigem ? -1 : 1; 
          }).map((propriedade: Propriedade) => {
            const labelId = `transfer-list-item-${propriedade.proCodigo}-label`;

            return (
              <ListItemButton
                key={propriedade.proCodigo}
                role="listitem"
                onClick={handleToggle(propriedade)}
              >
                <ListItemIcon>
                  { !(propriedadesVinculadas!.some((usuarioPropriedade: UsuarioPropriedade) => usuarioPropriedade.proOrigem === true && usuarioPropriedade.proCodigo === propriedade.proCodigo)) &&
                    <Checkbox
                      checked={checked.includes(propriedade)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                    />
                  }
                </ListItemIcon>
                <ListItemText id={labelId} primary={`${propriedade.proCodigo} - ${propriedade.proDescricao}`} />
              </ListItemButton>
            );
          })
          :
          <Grid item container mt={5} justifyContent={"center"}>
            { translations["lbl.react.cliente.fazendas.vincular"] }
          </Grid>
        }
      </List>
    </Paper>
  );

  
  //const leftChecked = intersection(checked, left);
  //const rightChecked = intersection(checked, right);

  return (
    <Grid container justifyContent="center">
      { translations &&
        <Grid item xs={12}>
          <div className="page-body">
            <span className="page-title">
              { translations["lbl.react.vincular.propriedades"] }
            </span>
            { loading === false && usuario ?
              <Grid container rowSpacing={2} justifyContent="center">
                <Grid item xs={12}>
                  <Grid container padding="22px" pr={0} mb={1}>
                    <Grid item xs={6} mb={5}>
                      <span onClick={() => window.history.back()} className="page-bread pointer">
                        { translations["lbl.react.usuarios"] }
                      </span>
                      <span className="page-bread">
                        { ` > ${translations["lbl.react.vincular.propriedades"]}` }
                      </span>
                    </Grid>
                    <Grid item xs={6} textAlign={"end"} flexDirection={"row"}>
                      <Button onClick={() => window.history.back()} className="btn-secundario btn-voltar" variant="contained"><ArrowBackIcon /> { translations["lbl.react.usuarios.voltar.lista"] }</Button>
                    </Grid>
                    <Grid item xs={12} className="page-title">
                      <span>
                      { usuario.usuNome }
                      </span>
                    </Grid>
                  </Grid>
                  <Box sx={{ width: "100%", backgroundColor: "#f7f7f7", border: "1px solid #f7f7f7", borderRadius: "3px"} }>
                    <Grid container padding="22px">
                      <Grid item container xs={12} mt={4}>
                        <Grid item xs={4}>

                        </Grid>
                      </Grid>
                      <Grid item container xs={12} mt={1}>
                        <Grid item xs={4}>

                        </Grid>
                      </Grid>
                      <Grid container mt={1} justifyContent={"space-between"}>
                        <Grid item>
                          <Switch
                            className="switch-protocolo"
                            checked={usuMaster}
                            onChange={handleUpdateUsuarioStatus}
                            inputProps={{ 'aria-label': 'controlled' }}
                            name='usuMaster'
                          />
                          <span className="input-label">{ translations["lbl.react.usuario.master.ativar.desativar"] }</span>
                        </Grid>
                        <Grid item>
                          <Button disabled={!usuMaster} onClick={() => handleSavePropriedades()} className="btn-primario" variant="contained">Salvar alterações</Button>
                        </Grid>
                      </Grid>
                      { usuMaster ?
                        <Grid item container xs={12} mt={4} justifyContent={"space-between"}>
                          <Grid item xs mb={2} pr={1}>
                            <ComboBox
                              label={"Buscar por cliente vinculados"}
                              options={clienteCombo.filter((cliente: Combo) => cliCodigoVinculados!.includes(cliente.id))}
                              handleChange={handleLeftComboChange}
                              value={valueComboLeft}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            
                          </Grid>
                          <Grid item xs mb={2} pl={1}>
                            <ComboBox
                              label={"Buscar por cliente"}
                              options={clienteCombo}
                              handleChange={handleRightComboChange}
                              value={valueComboRight}
                            />
                          </Grid>
                          <TransferListCustom 
                            left={left}
                            right={right}
                            handleToggleLeft={handleToggleLeft}
                            handleToggleRight={handleToggleRight}
                            handleAllRight={handleAllRight}
                            handleCheckedRight={handleCheckedRight}
                            handleCheckedLeft={handleCheckedLeft}
                            handleAllLeft={handleAllLeft}
                            leftChecked={checkedLeft}
                            rightChecked={checkedRight}
                            customList={customList}
                          />
                        </Grid>
                        :
                        <Grid xs={12} my={16} container sx={{backgroundColor: "#f2dede;"}} height={"50px"} justifyContent={"center"} alignItems={"center"}>
                            <Grid item mr={1}>
                              <img src={warningIcon} alt="novo cadastro icon" />
                            </Grid>
                            <Grid item color={"#dd191d"} fontWeight={400}>
                              <span>{translations["lbl.react.ative.usuario.vincular.fazendas"]}</span>
                            </Grid>
                        </Grid>
                      }
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              :
              <Loading setHeight="85vh"/>
            }
          </div>
        </Grid>
      }
    </Grid>
  );
}

export default UsuarioPropriedade;